import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";

import type { ProjectSoftwareRule } from "@zentrack/domain";
import {
  Banner,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldGroup,
  FieldLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@zentrack/ui";

import type { TrackingCapability } from "@zentrack/desktop-api";

export interface StartTimerDialogProps {
  initialNote?: string;
  initialProjectId?: string;
  onOpenChange: (open: boolean) => void;
  onStart: (values: { note: string; projectId: string }) => Promise<void>;
  open: boolean;
  projectLabel: (projectId: string) => string;
  projectSoftwareRules: ProjectSoftwareRule[];
  projects: Array<{ id: string }>;
  trackingCapability: TrackingCapability;
}

export function StartTimerDialog({
  initialNote = "",
  initialProjectId,
  onOpenChange,
  onStart,
  open,
  projectLabel,
  projectSoftwareRules,
  projects,
  trackingCapability,
}: StartTimerDialogProps) {
  const [projectId, setProjectId] = useState(initialProjectId ?? projects[0]?.id ?? "");
  const [note, setNote] = useState(initialNote);
  const projectsRef = useRef(projects);
  projectsRef.current = projects;

  useEffect(() => {
    if (!open) {
      return;
    }

    setProjectId(initialProjectId ?? projectsRef.current[0]?.id ?? "");
    setNote(initialNote);
  }, [initialNote, initialProjectId, open]);

  const activeSoftwareRules = useMemo(
    () => projectSoftwareRules.filter((rule) => rule.projectId === projectId),
    [projectId, projectSoftwareRules],
  );
  const hasSoftwareFilter = activeSoftwareRules.length > 0;
  const trackingWarning =
    hasSoftwareFilter && trackingCapability.status !== "supported"
      ? trackingCapability.message ??
        "Foreground-app filtering is unavailable right now. The timer can still run."
      : null;

  const projectOptions = projects.map((project) => ({
    value: project.id,
    label: projectLabel(project.id),
  }));

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="w-[min(92vw,640px)]">
        <form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            if (!projectId) {
              return;
            }

            void onStart({
              note: note.trim(),
              projectId,
            });
          }}
        >
          <DialogHeader>
            <DialogTitle>Start timer</DialogTitle>
            <DialogDescription>Choose a project and start the session.</DialogDescription>
          </DialogHeader>

          <FieldGroup className="grid gap-4">
            <Field>
              <FieldLabel htmlFor="start-project">Project</FieldLabel>
              <Select
                items={projectOptions}
                onValueChange={(value) => {
                  if (value !== null) setProjectId(value);
                }}
                value={projectId}
              >
                <SelectTrigger className="w-full" id="start-project">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {projectOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <FieldLabel htmlFor="start-note">Note</FieldLabel>
              <Textarea
                id="start-note"
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setNote(event.target.value)}
                placeholder="Optional note"
                value={note}
              />
            </Field>

            {hasSoftwareFilter ? (
              <div className="rounded-lg border border-border/75 bg-secondary/30 px-4 py-3">
                <p className="text-sm font-medium text-foreground">Tracked software</p>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  {activeSoftwareRules.map((rule) => rule.displayName).join(", ")}
                </p>
              </div>
            ) : null}

            {trackingWarning ? <Banner message={trackingWarning} tone="warning" /> : null}
          </FieldGroup>

          <DialogFooter className="border-t border-border/70 pt-4">
            <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
              Cancel
            </Button>
            <Button disabled={!projectId} type="submit">
              Start timer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
